<template>
  <div class="waste-uploader">
    <div
      class="waste-uploader__drop"
      :class="{ 'is-over': over, 'is-disabled': disabled }"
      role="button"
      :aria-disabled="String(disabled)"
      :tabindex="disabled ? -1 : 0"
      :aria-label="$t('waste_add_photos')"
      @click="pick"
      @keydown.enter.prevent="pick"
      @keydown.space.prevent="pick"
      @dragover.prevent="over = !disabled"
      @dragleave.prevent="over = false"
      @drop.prevent="onDrop"
    >
      <span class="waste-uploader__icon">
        <v-icon size="20" color="primary">fal fa-camera</v-icon>
      </span>
      <div class="waste-uploader__text">
        <div class="waste-uploader__title">{{ $t("waste_add_photos") }}</div>
        <div class="waste-uploader__hint">
          {{ $t("waste_evidence_hint", { max, size }) }}
        </div>
      </div>
      <span class="waste-uploader__count">{{ previews.length }}/{{ max }}</span>
    </div>

    <input
      ref="input"
      type="file"
      class="waste-uploader__input"
      accept="image/jpeg,image/png,image/webp"
      multiple
      @change="onPick"
    />

    <div v-if="previews.length" class="waste-uploader__grid">
      <div v-for="item in previews" :key="item.id" class="waste-uploader__thumb">
        <img :src="item.url" :alt="item.name" />
        <button
          type="button"
          class="waste-uploader__remove"
          :disabled="disabled"
          :aria-label="$t('waste_remove_photo') + ': ' + item.name"
          @click.stop="remove(item)"
        >
          <v-icon x-small>fal fa-times</v-icon>
        </button>
      </div>
    </div>

    <div v-if="error" class="waste-uploader__error" role="alert">{{ error }}</div>
  </div>
</template>

<script>
import {
  WASTE_MAX_IMAGES,
  WASTE_MAX_IMAGE_MB,
  WASTE_IMAGE_TYPES,
} from "@/mixin/waste";

let seq = 0;

export default {
  name: "WasteImageUploader",

  props: {
    value: { type: Array, default: () => [] },
    max: { type: Number, default: WASTE_MAX_IMAGES },
    size: { type: Number, default: WASTE_MAX_IMAGE_MB },
    disabled: { type: Boolean, default: false },
  },

  data: () => ({
    over: false,
    error: "",
    previews: [],
  }),

  watch: {
    // Rebuild previews from the parent's list, reusing object URLs for files
    // that are still there and revoking the ones that were removed.
    value: {
      immediate: true,
      handler(files) {
        const keep = new Map(this.previews.map((p) => [p.file, p]));
        const next = (files || []).map((file) => {
          const existing = keep.get(file);
          if (existing) {
            keep.delete(file);
            return existing;
          }
          return {
            id: ++seq,
            file,
            name: file.name,
            url: URL.createObjectURL(file),
          };
        });
        keep.forEach((p) => URL.revokeObjectURL(p.url));
        this.previews = next;
      },
    },
  },

  beforeDestroy() {
    this.previews.forEach((p) => URL.revokeObjectURL(p.url));
  },

  methods: {
    pick() {
      if (this.disabled) return;
      this.$refs.input.click();
    },

    onPick(event) {
      this.add(Array.from(event.target.files || []));
      // allow picking the same file again after removing it
      event.target.value = "";
    },

    onDrop(event) {
      this.over = false;
      if (this.disabled) return;
      this.add(Array.from(event.dataTransfer.files || []));
    },

    add(files) {
      this.error = "";
      const accepted = [];

      for (const file of files) {
        if (!WASTE_IMAGE_TYPES.includes(file.type)) {
          this.error = this.$t("waste_photo_type_invalid", { name: file.name });
          continue;
        }
        if (file.size > this.size * 1024 * 1024) {
          this.error = this.$t("waste_photo_too_large", {
            name: file.name,
            size: this.size,
          });
          continue;
        }
        if (this.previews.length + accepted.length >= this.max) {
          this.error = this.$t("waste_photo_limit", { max: this.max });
          break;
        }
        accepted.push(file);
      }

      if (accepted.length) {
        this.$emit("input", [...this.value, ...accepted]);
      }
    },

    remove(item) {
      this.error = "";
      this.$emit(
        "input",
        this.value.filter((file) => file !== item.file)
      );
    },
  },
};
</script>

<style scoped>
.waste-uploader__drop {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.waste-uploader__drop:hover,
.waste-uploader__drop:focus-visible,
.waste-uploader__drop.is-over {
  border-color: var(--v-primary-base, #5b39c9);
  background: #f8fafc;
  outline: none;
}

.waste-uploader__drop.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.waste-uploader__icon {
  display: inline-flex;
  flex: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #f1f5f9;
}

.waste-uploader__text {
  flex: 1;
  min-width: 0;
}

.waste-uploader__title {
  color: #1e293b;
  font-size: 13px;
  font-weight: 600;
}

.waste-uploader__hint {
  color: #64748b;
  font-size: 11px;
  line-height: 1.4;
}

.waste-uploader__count {
  flex: none;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  background: #f1f5f9;
  border-radius: 12px;
}

.waste-uploader__input {
  display: none;
}

.waste-uploader__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.waste-uploader__thumb {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-field-bg);
}

.waste-uploader__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.waste-uploader__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--app-border);
  border-radius: 50%;
  background: var(--app-card-bg);
  color: var(--app-text-primary);
  cursor: pointer;
}

.waste-uploader__remove:hover {
  border-color: var(--app-error-soft-border);
  background: var(--app-error-soft-bg);
}

.waste-uploader__error {
  margin-top: 8px;
  padding: 8px 12px;
  border: 1px solid var(--app-error-soft-border);
  border-radius: 10px;
  background: var(--app-error-soft-bg);
  color: var(--app-text-primary);
  font-size: 12px;
}

.theme--dark .waste-uploader__drop {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .waste-uploader__drop:hover,
.theme--dark .waste-uploader__drop:focus-visible,
.theme--dark .waste-uploader__drop.is-over {
  border-color: #818cf8;
  background: #2d2d44;
}

.theme--dark .waste-uploader__icon {
  background: #1e1e2d;
}

.theme--dark .waste-uploader__title {
  color: #f1f5f9;
}

.theme--dark .waste-uploader__hint {
  color: #94a3b8;
}

.theme--dark .waste-uploader__count {
  color: #cbd5e1;
  background: #1e1e2d;
}

.theme--dark .waste-uploader__thumb {
  border-color: rgba(255, 255, 255, 0.12);
  background: #1e1e2d;
}

.theme--dark .waste-uploader__remove {
  border-color: rgba(255, 255, 255, 0.14);
  background: #1e1e2d;
  color: #f1f5f9;
}
</style>
